// src/pages/AssignShippingService.jsx

import React, { useState, useEffect } from 'react';
import { PiPencilBold } from 'react-icons/pi';
import { Link } from 'react-router-dom';
import Header from '../../../header/header';
import Navvbar from '../../../header/Navvbar';
import Footer from '../../../footer';
import './AssignShippingService.css';

const AssignShippingService = () => {
  const [shippingTypes, setShippingTypes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedShippingType, setSelectedShippingType] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get userLabId from localStorage
  const userLabId = localStorage.getItem('userLabId');

  /* ------------------------------------------------------------------ */
  /* FETCH DATA                                                         */
  /* ------------------------------------------------------------------ */

  const fetchShippingTypes = async () => {
    if (!userLabId) {
      setError('User not logged in or lab ID missing');
      return;
    }

    try {
      // Fetch shipping types for the current user's lab only
      const res = await fetch(
        `https://hms.backend.hiragroupcloud.com/api/shipping?addedBy=${userLabId}`
      );

      const json = await res.json();

      if (json.success && Array.isArray(json.data)) {
        setShippingTypes(json.data);
      } else {
        setError('Failed to load shipping types');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    }
  };

  const fetchAccounts = async () => {
    if (!userLabId) {
      setError('User not logged in or lab ID missing');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`
      );

      const json = await res.json();

      if (res.ok && json.success && Array.isArray(json.data)) {
        setAccounts(json.data);
      } else {
        setError(
          json.message || 'Failed to load accounts'
        );
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userLabId) {
      Promise.all([
        fetchShippingTypes(),
        fetchAccounts(),
      ]);
    } else {
      setError('User not logged in or lab ID missing');
      setLoading(false);
    }
  }, [userLabId]);

  /* ------------------------------------------------------------------ */
  /* CHECKBOX LOGIC                                                     */
  /* ------------------------------------------------------------------ */

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(
        accounts.map((a) => a._id)
      );
    }

    setSelectAll(!selectAll);
  };

  const handleAccountToggle = (id) => {
    const newSel = selectedAccounts.includes(id)
      ? selectedAccounts.filter((x) => x !== id)
      : [...selectedAccounts, id];

    setSelectedAccounts(newSel);

    setSelectAll(
      newSel.length === accounts.length &&
        accounts.length > 0
    );
  };

  /* ------------------------------------------------------------------ */
  /* SAVE                                                               */
  /* ------------------------------------------------------------------ */

  const handleSave = async () => {
    if (!selectedShippingType) {
      return alert('Select a shipping type');
    }

    if (selectedAccounts.length === 0) {
      return alert('Select at least one account');
    }

    const payload = {
      shippingTypeId: selectedShippingType,
      accountIds: selectedAccounts,
    };

    try {
      const res = await fetch(
        'https://hms.backend.hiragroupcloud.com/api/shipping/assign',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();

      if (res.ok && json.success) {
        alert('Assigned successfully!');

        fetchAccounts();

        setSelectedAccounts([]);
        setSelectAll(false);
      } else {
        alert(
          'Failed: ' +
            (json.message || 'unknown')
        );
      }
    } catch (e) {
      alert('Save error: ' + e.message);
    }
  };

  /* ------------------------------------------------------------------ */
  /* LOADING                                                            */
  /* ------------------------------------------------------------------ */

  if (loading) {
    return (
      <>
        <Header />
        <Navvbar />

        <div className="assign-shipping-loading">
          Loading…
        </div>

        <Footer />
      </>
    );
  }

  /* ------------------------------------------------------------------ */
  /* ERROR                                                              */
  /* ------------------------------------------------------------------ */

  if (error) {
    return (
      <>
        <Header />
        <Navvbar />

        <div className="assign-shipping-error">
          {error}

          <button
            onClick={() =>
              window.location.reload()
            }
          >
            Retry
          </button>
        </div>

        <Footer />
      </>
    );
  }

  /* ------------------------------------------------------------------ */
  /* USER LOGIN CHECK                                                   */
  /* ------------------------------------------------------------------ */

  if (!userLabId) {
    return (
      <>
        <Header />
        <Navvbar />

        <div className="assign-shipping-error">
          User ID not found. Please log in again.
        </div>

        <Footer />
      </>
    );
  }

  /* ------------------------------------------------------------------ */
  /* RENDER                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <>
      <div className="assign-shipping-header">
        <Header />
        <Navvbar />
      </div>

      <div className="assign-shipping-wrapper">
        <div className="row assign-shipping-row">

          {/* ---------- LEFT SIDEBAR ---------- */}

          <div
            className="col-md-2 assign-shipping-sidebar"
          >
            <h2>Settings</h2>

            {[
              {
                to: '/Tabs',
                label: 'Lab Details',
              },
              {
                to: '/',
                label: 'Store',
              },
              {
                to: '/usersetting',
                label: 'Users',
              },
              {
                to: '/',
                label: 'Permission Groups',
              },
              {
                to: '/Account',
                label: 'Accounts',
              },
              {
                to: '/Owerviews',
                label: 'Finances',
              },
              {
                to: '/owersetting',
                label: 'Case Settings',
                bold: true,
              },
            ].map((it, i) => (
              <div key={i}>
                <Link
                  to={it.to}
                  className={
                    it.bold
                      ? 'assign-shipping-sidebar-link assign-shipping-bold-link'
                      : 'assign-shipping-sidebar-link'
                  }
                >
                  {it.label}
                </Link>
              </div>
            ))}
          </div>

          {/* ---------- RIGHT CONTENT ---------- */}

          <div className="col-md-10 assign-shipping-content">

            <h1>
              Assign Shipping Service
            </h1>

            {/* ---- SHIPPING TYPE ---- */}

            <h2>
              Shipping Types
            </h2>

            <div className="shipping-type-box">

              <label>
                Shipping Type{' '}
                <span className="required-star">
                  *
                </span>
              </label>

              <select
                value={selectedShippingType}
                onChange={(e) =>
                  setSelectedShippingType(
                    e.target.value
                  )
                }
              >
                <option value="">
                  Select Shipping Type
                </option>

                {shippingTypes.length > 0 ? (
                  shippingTypes.map((t) => (
                    <option
                      key={t._id}
                      value={t._id}
                    >
                      {t.service} ({t.company})
                    </option>
                  ))
                ) : (
                  <option
                    value=""
                    disabled
                  >
                    No shipping types found. Create one first.
                  </option>
                )}
              </select>

              {/* Shipping types count indicator */}

              {shippingTypes.length > 0 && (
                <div className="shipping-count">
                  Showing{' '}
                  {shippingTypes.length}{' '}
                  shipping type
                  {shippingTypes.length !== 1
                    ? 's'
                    : ''}{' '}
                  from your lab
                </div>
              )}
            </div>

            {/* ---- ACCOUNTS ---- */}

            <h2>
              Accounts
            </h2>

            <div className="accounts-box">

              {/* SELECT ALL */}

              <div className="select-all-row">

                <label>
                  Select All
                </label>

                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={
                    handleSelectAll
                  }
                />
              </div>

              {/* LIST */}

              {accounts.length === 0 ? (
                <p className="no-accounts">
                  No accounts found for your lab.
                </p>
              ) : (
                accounts.map((acc) => (
                  <div
                    key={acc._id}
                    className="account-row"
                  >
                    <label
                      className="account-name"
                      title={acc.account_name}
                    >
                      {acc.account_name}
                    </label>

                    <div className="account-options">

                      <input
                        type="checkbox"
                        checked={selectedAccounts.includes(
                          acc._id
                        )}
                        onChange={() =>
                          handleAccountToggle(
                            acc._id
                          )
                        }
                      />

                      {/* Show currently assigned shipping */}

                      {acc.shippingService && (
                        <span
                          className="shipping-service"
                          title={`Company: ${acc.shippingService.company}`}
                        >
                          {
                            acc.shippingService
                              .service
                          }
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}

              <p className="account-info">
                This will remove account level
                transit days overrides.
              </p>

              <button
                className="save-button"
                onClick={handleSave}
                disabled={
                  !selectedShippingType ||
                  selectedAccounts.length === 0
                }
              >
                <PiPencilBold />

                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="assign-shipping-footer">
        <Footer />
      </div>
    </>
  );
};

export default AssignShippingService;